// 定义资源接口（与 ResourcesPopup 中的一致）
export interface Resource {
  id: string;
  type: 'image' | 'text';
  content: string;
  aiUrl?: string;
  name: string;
  order: number;
  // 可选：图片资源的宽高
  width?: number;
  height?: number;
}

// 使用本地存储持久化全局资源
const STORAGE_KEY = 'GiftMkr_globalResources';
const loadStoredResources = (): Resource[] => {
  try {
    const raw =
      typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

// 全局资源状态
let globalResources: Resource[] = loadStoredResources();
let resourceListeners: ((resources: Resource[]) => void)[] = [];

// 更新全局资源
export const updateGlobalResources = (resources: Resource[]) => { 
  const dedupedResources: Resource[] = (() => {
    const seen = new Set<string>();
    const result: Resource[] = [];
    for (const r of resources) {
      const content = (r.content || '').trim();
      const aiRaw = (r as any)?.ai;
      const ai = typeof aiRaw === 'string' ? aiRaw.trim() : '';
      // 当 ai 存在时，用 content+ai 作为唯一键；否则仅用 content
      const key = ai ? `${content}||${ai}` : content;

      if (key && seen.has(key)) {
        continue;
      }
      result.push(r);
      if (key) seen.add(key);
    }
    return result;
  })();
 
  globalResources = dedupedResources;
 
  // 写入本地存储以便刷新后保留
  // try {
  //   if (typeof window !== 'undefined') {
  //     window.localStorage.setItem(STORAGE_KEY, JSON.stringify(resources));
  //   }
  // } catch {}

  // 通知所有监听器
  // 通知监听者使用去重后的资源
  resourceListeners.forEach(listener => listener(dedupedResources));

  // 异步测量图片宽高，并再次更新（不阻塞首次更新）
  (async () => {
    const measured: Resource[] = await Promise.all(
      dedupedResources.map(async (r) => {
        if (r.type !== 'image' || !r.content || r.width || r.height) return r;
        const dims = await getImageDimensions(r.content+"?x-oss-process=style/large").catch(() => null);
        if (dims && dims.width && dims.height) {
          return { ...r, width: dims.width, height: dims.height };
        }
        return r;
      })
    );
    globalResources = measured;
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(measured));
      }
    } catch {}
    resourceListeners.forEach(listener => listener(measured));
  })();
};

// 获取当前全局资源
export const getGlobalResources = (): Resource[] => {
  return globalResources;
};

// 订阅资源变化
export const subscribeToResources = (listener: (resources: Resource[]) => void) => {
  resourceListeners.push(listener);
  return () => {
    resourceListeners = resourceListeners.filter(l => l !== listener);
  };
};

// 加载图片并获取其原始宽高（不触发跨域 Canvas 限制）
function getImageDimensions(url: string): Promise<{ width: number; height: number }> {
 
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      return reject(new Error('No window environment'));
    }
    const img = new Image();
    img.onload = () => {
      console.log('getImageDimensions', url, img.naturalWidth, img.naturalHeight);
      resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height });
    };
    img.onerror = (e) => reject(e);
    img.src = url;
  });
}


const CACHE_PREFIX = 'GiftMkr_ai_cache:';
export function getCache(key: string): { timestamp: number; response: any } | undefined {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return undefined;
    const raw = window.localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.timestamp !== 'number') return undefined;
    return parsed;
  } catch {
    return undefined;
  }
}

export function setCache(key: string, value: { timestamp: number; response: any }): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
  } catch {
    // 忽略写入错误（例如存储满、隐私模式等）
  }
}

